import { Capacitor } from '@capacitor/core';
import {
  AlertTriangle, ArrowLeft, BookOpen, Briefcase, Building2,
  ChevronDown, ChevronLeft, ChevronRight, Clock, Code2, ExternalLink, FileText,
  MessageCircle, Play, TrendingUp, Video, X, Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { supabase } from '../../../lib/supabase';
import { getOptimizationHistory } from '../../../services/optimizationService';
import { getJobApplications } from '../../../services/applicationTrackingService';
import type { JobApplication } from '../../../services/applicationTrackingService';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Types
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

type JobApplicationRow = {
  id: string;             // = optimization_id (real or synthetic `agent-<uuid>`)
  job_title: string;
  company_name: string;
  job_url: string | null;
  status: string;
  created_at: string;
  job_description?: string | null;
  /**
   * Agent flow back-reference. When set, this row was synthesised from a
   * job_applications row (the agent never writes to llm_optimization_levelX)
   * and the fetch-optimization-context effect can short-circuit using the
   * artifacts on the application directly instead of hitting the empty
   * llm_optimization_levelX tables.
   */
  _agentApplication?: JobApplication;
};

type TopicCategory = 'behavioral' | 'company' | 'role' | 'leadership' | 'technical' | 'keyword';

type CourseRecommendation = {
  title: string;
  reason: string;
  youtubeUrl: string;
  videoId?: string | null;
  duration?: string | null;   // e.g. "14m", "1h 20m"
  channel?: string | null;
  priority: number;
  category: TopicCategory;
};

/** Optimization context fetched per job when modal opens */
type OptimizationContext = {
  skillGaps: string[];       // skills missing from resume vs JD
  improvements: string[];    // what was improved  = resume strengths
  warnings: string[];        // remaining issues   = weaknesses
  experienceLevel: string;   // e.g. "mid", "senior"
  matchScore: number;        // 0-100
  resumeText: string;        // full optimized resume text
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Category display configuration
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const CATEGORY_META: Record<TopicCategory, { label: string; textColor: string; bgColor: string; Icon: React.ElementType }> = {
  behavioral: { label: 'Behavioral',           textColor: 'text-purple-600 dark:text-purple-400',   bgColor: 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-700',    Icon: MessageCircle },
  company:    { label: 'Company Research',      textColor: 'text-blue-600 dark:text-blue-400',       bgColor: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700',            Icon: Building2 },
  role:       { label: 'Role-Specific',         textColor: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700', Icon: Briefcase },
  leadership: { label: 'Leadership & Level',    textColor: 'text-orange-600 dark:text-orange-400',   bgColor: 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-700',    Icon: TrendingUp },
  technical:  { label: 'Technical Skills',      textColor: 'text-indigo-600 dark:text-indigo-400',   bgColor: 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-700',    Icon: Code2 },
  keyword:    { label: 'From Job Description',  textColor: 'text-gray-500 dark:text-gray-400',       bgColor: 'bg-gray-100 border-gray-200 dark:bg-gray-800/50 dark:border-gray-600',           Icon: FileText },
};

const CATEGORY_DISPLAY_ORDER: TopicCategory[] = ['behavioral', 'company', 'role', 'leadership', 'technical', 'keyword'];

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Stop-words
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const STOPWORDS = new Set([
  'a','an','and','are','as','at','be','by','for','from','has','have','in','is','it','its','of',
  'on','or','our','that','the','their','they','this','to','was','we','will','with','you','your',
  'role','roles','work','working','job','candidate','responsibilities','responsibility',
  'requirements','required','preferred','experience','years','skills','skill','ability',
  'strong','excellent','including','such','also','may','must','well','highly','able','good',
  'using','team','cross','related','knowledge','understanding','proven','demonstrated',
]);

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Expanded skill catalogue â€” { keyword, display }
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const KNOWN_SKILLS: { keyword: string; display: string }[] = [
  // Frontend
  { keyword: 'react',         display: 'React' },
  { keyword: 'next.js',       display: 'Next.js' },
  { keyword: 'nextjs',        display: 'Next.js' },
  { keyword: 'vue',           display: 'Vue.js' },
  { keyword: 'angular',       display: 'Angular' },
  { keyword: 'typescript',    display: 'TypeScript' },
  { keyword: 'javascript',    display: 'JavaScript' },
  { keyword: 'svelte',        display: 'Svelte' },
  { keyword: 'tailwind',      display: 'Tailwind CSS' },
  { keyword: 'redux',         display: 'Redux' },
  { keyword: 'webpack',       display: 'Webpack' },
  { keyword: 'vite',          display: 'Vite' },
  // Backend
  { keyword: 'node',          display: 'Node.js' },
  { keyword: 'express',       display: 'Express.js' },
  { keyword: 'nestjs',        display: 'NestJS' },
  { keyword: 'python',        display: 'Python' },
  { keyword: 'django',        display: 'Django' },
  { keyword: 'flask',         display: 'Flask' },
  { keyword: 'fastapi',       display: 'FastAPI' },
  { keyword: 'java',          display: 'Java' },
  { keyword: 'spring',        display: 'Spring Boot' },
  { keyword: 'c#',            display: 'C#' },
  { keyword: '.net',          display: '.NET' },
  { keyword: 'go',            display: 'Go / Golang' },
  { keyword: 'golang',        display: 'Go / Golang' },
  { keyword: 'rust',          display: 'Rust' },
  { keyword: 'php',           display: 'PHP' },
  { keyword: 'laravel',       display: 'Laravel' },
  { keyword: 'ruby',          display: 'Ruby on Rails' },
  // Database / Data
  { keyword: 'sql',           display: 'SQL' },
  { keyword: 'postgresql',    display: 'PostgreSQL' },
  { keyword: 'postgres',      display: 'PostgreSQL' },
  { keyword: 'mysql',         display: 'MySQL' },
  { keyword: 'mongodb',       display: 'MongoDB' },
  { keyword: 'redis',         display: 'Redis' },
  { keyword: 'elasticsearch', display: 'Elasticsearch' },
  { keyword: 'dynamodb',      display: 'DynamoDB' },
  { keyword: 'snowflake',     display: 'Snowflake' },
  { keyword: 'bigquery',      display: 'BigQuery' },
  { keyword: 'dbt',           display: 'dbt' },
  // Cloud / Infra
  { keyword: 'aws',           display: 'AWS' },
  { keyword: 'gcp',           display: 'GCP' },
  { keyword: 'azure',         display: 'Azure' },
  { keyword: 'docker',        display: 'Docker' },
  { keyword: 'kubernetes',    display: 'Kubernetes' },
  { keyword: 'k8s',           display: 'Kubernetes' },
  { keyword: 'terraform',     display: 'Terraform' },
  { keyword: 'ansible',       display: 'Ansible' },
  { keyword: 'jenkins',       display: 'Jenkins' },
  { keyword: 'github actions',display: 'GitHub Actions' },
  // Data / ML / AI
  { keyword: 'machine learning', display: 'Machine Learning' },
  { keyword: 'deep learning',    display: 'Deep Learning' },
  { keyword: 'tensorflow',       display: 'TensorFlow' },
  { keyword: 'pytorch',          display: 'PyTorch' },
  { keyword: 'scikit-learn',     display: 'Scikit-learn' },
  { keyword: 'pandas',           display: 'Pandas' },
  { keyword: 'spark',            display: 'Apache Spark' },
  { keyword: 'kafka',            display: 'Apache Kafka' },
  { keyword: 'airflow',          display: 'Apache Airflow' },
  { keyword: 'llm',              display: 'LLMs / Generative AI' },
  { keyword: 'langchain',        display: 'LangChain' },
  { keyword: 'openai',           display: 'OpenAI API' },
  // Architecture
  { keyword: 'system design',    display: 'System Design' },
  { keyword: 'microservices',    display: 'Microservices' },
  { keyword: 'rest',             display: 'REST APIs' },
  { keyword: 'graphql',          display: 'GraphQL' },
  { keyword: 'grpc',             display: 'gRPC' },
  { keyword: 'event-driven',     display: 'Event-Driven Architecture' },
  { keyword: 'data structures',  display: 'Data Structures & Algorithms' },
  { keyword: 'algorithms',       display: 'Algorithms' },
  { keyword: 'dsa',              display: 'DSA' },
  // Process
  { keyword: 'ci/cd',            display: 'CI/CD' },
  { keyword: 'devops',           display: 'DevOps' },
  { keyword: 'agile',            display: 'Agile' },
  { keyword: 'scrum',            display: 'Scrum' },
  { keyword: 'git',              display: 'Git' },
  // Testing
  { keyword: 'jest',             display: 'Jest' },
  { keyword: 'cypress',          display: 'Cypress' },
  { keyword: 'selenium',         display: 'Selenium' },
  { keyword: 'playwright',       display: 'Playwright' },
  // Mobile
  { keyword: 'react native',     display: 'React Native' },
  { keyword: 'flutter',          display: 'Flutter' },
  { keyword: 'swift',            display: 'Swift / iOS' },
  { keyword: 'kotlin',           display: 'Kotlin / Android' },
  // Other
  { keyword: 'figma',            display: 'Figma' },
  { keyword: 'tableau',          display: 'Tableau' },
  { keyword: 'power bi',         display: 'Power BI' },
  { keyword: 'excel',            display: 'Excel' },
  { keyword: 'jira',             display: 'Jira' },
];

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Helpers
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function youtubeSearchUrl(query: string) {
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(`${query} complete guide`)}`;
}

/** Extract lowercase skill keywords mentioned in a resume text */
function parseResumeSkills(resumeText: string): Set<string> {
  const lower = resumeText.toLowerCase();
  const found = new Set<string>();
  for (const s of KNOWN_SKILLS) {
    const re = new RegExp(`\\b${escapeRegExp(s.keyword)}\\b`, 'i');
    if (re.test(lower)) found.add(s.keyword.toLowerCase());
  }
  return found;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Comprehensive topic extraction â€” 6 categories, personalised via OptContext
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

type TopicItem = { topic: string; reason: string; priority: number; category: TopicCategory; urgent?: boolean };

function extractTopics(
  jobTitle: string,
  jobDescription: string,
  companyName?: string,
  ctx?: OptimizationContext
): TopicItem[] {
  const title = (jobTitle || '').toLowerCase();
  const text  = `${jobTitle}\n${jobDescription}`.toLowerCase();

  const items: TopicItem[] = [];
  const seen = new Set<string>();
  const add = (topic: string, reason: string, priority: number, category: TopicCategory, urgent = false) => {
    if (!topic.trim()) return;
    const key = topic.trim().toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ topic: topic.trim(), reason, priority, category, urgent });
  };

  // â”€â”€ PRIO 0 Â· Behavioral â€” always â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  add('Behavioral interview STAR method â€” complete guide',          'Essential framework for every round',            0, 'behavioral');
  add('"Tell me about yourself" â€” best structured answers',         'Opens almost every interview',                   0, 'behavioral');
  add('Why do you want this job? â€” compelling answers',             'Classic question in every round',                0, 'behavioral');
  add('Strengths and weaknesses â€” honest, impactful answers',       'Standard HR screening question',                 0, 'behavioral');
  add('Conflict resolution at work â€” STAR examples',               'Demonstrates EQ and professionalism',            0, 'behavioral');
  add('Salary negotiation scripts and tactics',                     'Know your worth before the offer stage',         0, 'behavioral');

  // Low match score or gaps â†’ add extra behavioral coaching
  if (ctx && ctx.matchScore > 0 && ctx.matchScore < 65) {
    add('How to interview for a stretch role â€” standing out despite gaps', 'Your resume match score suggests skill gaps', 0, 'behavioral', true);
    add('Addressing experience gaps confidently in interviews',            'Turn gaps into growth narrative',             0, 'behavioral', true);
  }

  // â”€â”€ PRIO 0 Â· Company Research â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const co = (companyName || '').trim();
  const coIsGeneric = !co || /^(company|unknown|n\/a)$/i.test(co);
  if (!coIsGeneric) {
    add(`${co} interview process, rounds and tips`,      `Specific to ${co}`,                         0, 'company');
    add(`${co} culture, values and what they look for`,  `Understand what ${co} prioritises`,         0, 'company');
  }
  add('How to research a company before an interview â€” full guide', 'Glassdoor, LinkedIn, news, 10-K strategy', 0, 'company');
  add('Best questions to ask the interviewer â€” complete list',      'Shows genuine interest and preparation',    0, 'company');

  // â”€â”€ PRIO 1 Â· Seniority / Level â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const expLevel  = (ctx?.experienceLevel || '').toLowerCase();
  const isIntern  = /(intern|internship|co-op|coop)/.test(title);
  const isJunior  = /(junior|jr\b|entry.?level|graduate|fresher|associate\b)/.test(title) || /junior|entry/.test(expLevel);
  const isSenior  = /\b(senior|sr\b|principal|staff engineer|level [iv]+)/.test(title) || /senior/.test(expLevel);
  const isLead    = /\b(lead|tech lead|tech-lead|staff|principal)/.test(title);
  const isMgr     = /\b(manager|director|vp|head of|chief|cto|cpo|engineering manager)/.test(title);

  if (isIntern || isJunior) {
    add('Entry-level developer interview â€” what to expect',          'Targeted to your experience level',            1, 'leadership');
    add('Data structures and algorithms crash course for interviews','Core CS expected from junior candidates',     1, 'leadership');
    add('How to ace your first tech job interview â€” full guide',     'Mindset and practical tips for beginners',    1, 'leadership');
  }
  if (isSenior) {
    add('Senior engineer system design interview â€” full guide',      'Expected depth at senior level',              1, 'leadership');
    add('Code review, mentoring and ownership interview questions',  'Senior-level signals beyond coding',          1, 'leadership');
  }
  if (isLead) {
    add('Tech lead interview questions and preparation guide',       'Balance of technical and leadership',         1, 'leadership');
    add('Technical decision-making and architecture interview',      'Core expectation for lead roles',             1, 'leadership');
  }
  if (isMgr) {
    add('Engineering manager interview â€” complete preparation',      'People management depth required',            1, 'leadership');
    add('Hiring, performance management and team building interview','Direct-report management questions',          1, 'leadership');
    add('Cross-functional stakeholder management interview guide',   'Influencing without direct authority',        1, 'leadership');
    add('Engineering strategy and roadmap interview for managers',   'VP/Director level expectations',              1, 'leadership');
  }

  // â”€â”€ PRIO 1 Â· Role-specific â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  // Frontend
  if (/(frontend|front-end|front end|ui engineer|react developer|web developer|javascript developer|ui\/ux engineer)/.test(title)) {
    add('Frontend system design interview â€” complete guide',          'Core to your role',                           1, 'role');
    add('JavaScript and TypeScript deep-dive interview questions',    'Primary language for this role',              1, 'role');
    add('CSS Flexbox, Grid and responsive design interview',          'UI fundamentals',                             1, 'role');
    add('Web performance, Core Web Vitals and lazy-loading interview','Key frontend optimisation skill',             1, 'role');
  }
  // Backend
  if (/(backend|back-end|back end|server.?side|api developer|java developer|python developer|node developer|platform engineer)/.test(title)) {
    add('Backend system design â€” scalable APIs complete guide',       'Core to your role',                           1, 'role');
    add('Database design, indexing, SQL and NoSQL interview',         'Backend fundamentals',                        1, 'role');
    add('REST API design best practices â€” interview preparation',     'API knowledge required',                      1, 'role');
    add('Caching, queuing and distributed systems interview',         'Scalability and performance depth',           1, 'role');
  }
  // Full-Stack
  if (/(full.?stack|fullstack)/.test(title)) {
    add('Full stack developer interview â€” complete guide',            'Core to your role',                           1, 'role');
    add('System design for full stack engineers',                     'Frontend + backend depth expected',           1, 'role');
  }
  // Data Science / ML / AI
  if (/(data scientist|machine learning|ml engineer|ai engineer|deep learning|nlp|computer vision|research scientist)/.test(title)) {
    add('Machine learning interview â€” statistics, algorithms, design','Core to your role',                           1, 'role');
    add('Statistics and probability for data science interviews',     'ML fundamentals',                             1, 'role');
    add('ML system design â€” feature stores, serving, monitoring',    'Senior ML/DS expected skill',                 1, 'role');
  }
  // Data Analyst / BI
  if (/(data analyst|business analyst|bi developer|business intelligence|analytics engineer|data engineer)/.test(title)) {
    add('SQL for data analysts â€” complete interview guide',           'Core to your role',                           1, 'role');
    add('Data visualisation and storytelling interview',              'BI/Analyst must-have',                        1, 'role');
    add('Business metrics, KPIs and North Star interview',            'Analytical thinking depth',                   1, 'role');
  }
  // DevOps / SRE
  if (/(devops|site reliability|sre\b|platform engineer|infrastructure|cloud engineer|devsecops)/.test(title)) {
    add('DevOps interview â€” CI/CD, pipelines and automation',         'Core to your role',                           1, 'role');
    add('Kubernetes and Docker deep-dive interview guide',            'Container orchestration depth',               1, 'role');
    add('SRE interview â€” incident response, SLOs and on-call',       'Reliability engineering',                     1, 'role');
  }
  // Mobile
  if (/(mobile developer|ios developer|android developer|react native|flutter developer|swift|kotlin)/.test(title)) {
    add('Mobile app development interview â€” complete guide',          'Core to your role',                           1, 'role');
    add('Mobile architecture patterns â€” MVVM, MVI, clean arch',      'Architecture depth for mobile',               1, 'role');
  }
  // QA / SDET
  if (/(qa engineer|quality assurance|test engineer|sdet|automation engineer|tester)/.test(title)) {
    add('Software testing interview â€” complete preparation guide',    'Core to your role',                           1, 'role');
    add('Test automation interview â€” Selenium, Cypress, Playwright',  'Automation depth required',                   1, 'role');
    add('Testing strategy, test plans and coverage interview',        'Quality process knowledge',                   1, 'role');
  }
  // Product Manager
  if (/(product manager|product owner|\bpm\b|head of product|vp of product)/.test(title)) {
    add('Product manager interview â€” complete preparation guide',     'Core to your role',                           1, 'role');
    add('Product prioritisation frameworks â€” RICE, MoSCoW, JTBD',   'PM decision-making depth',                    1, 'role');
    add('Product metrics, OKRs and North Star interview',             'Data-driven PM thinking',                     1, 'role');
    add('Product roadmap and stakeholder interview guide',            'PM strategy questions',                       1, 'role');
  }
  // UX / Product Design
  if (/(ux designer|ui designer|product designer|ux researcher|interaction designer)/.test(title)) {
    add('UX/UI designer portfolio and process interview guide',       'Core to your role',                           1, 'role');
    add('Design thinking, user research and usability interview',     'Design process depth',                        1, 'role');
  }
  // Security
  if (/(security engineer|cybersecurity|information security|appsec|penetration|devsecops|cloud security)/.test(title)) {
    add('Cybersecurity engineer interview â€” complete guide',          'Core to your role',                           1, 'role');
    add('OWASP Top 10 and application security interview',            'Security fundamentals',                       1, 'role');
  }
  // Solutions Architect
  if (/(architect|solutions architect|enterprise architect|cloud architect)/.test(title)) {
    add('Software architect interview â€” complete preparation',        'Core to your role',                           1, 'role');
    add('Distributed systems design interview â€” advanced guide',      'Architecture depth expected',                 1, 'role');
    add('Cloud architecture patterns â€” AWS, Azure, GCP interview',   'Multi-cloud knowledge',                       1, 'role');
  }
  // Scrum / Agile
  if (/(scrum master|agile coach|delivery manager|program manager)/.test(title)) {
    add('Scrum master interview questions and answers guide',         'Core to your role',                           1, 'role');
    add('Agile facilitation and team ceremonies interview',           'Coaching depth',                              1, 'role');
  }
  // Generic engineering fallback
  if (/(engineer|developer|programmer|software)/.test(title) && !items.some(t => t.category === 'role')) {
    add('Software engineer system design â€” complete guide',           'Core engineering expectation',                1, 'role');
    add('Coding interview â€” data structures and algorithms guide',    'CS fundamentals',                             1, 'role');
  }

  // â”€â”€ PRIO 2 Â· Personalised skill gaps (from optimization context) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (ctx && ctx.skillGaps.length > 0) {
    for (const gap of ctx.skillGaps.slice(0, 6)) {
      const clean = (typeof gap === 'string' ? gap : (gap as any)?.text ?? '').trim();
      if (!clean) continue;
      add(
        `${clean} â€” how to answer interview questions as a learner`,
        `Skill gap identified in your resume vs this job`,
        2, 'technical', true
      );
    }
  }

  // â”€â”€ PRIO 2 Â· Technical skills matched from JD â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const resumeSkills = ctx?.resumeText ? parseResumeSkills(ctx.resumeText) : new Set<string>();

  for (const skill of KNOWN_SKILLS) {
    const re = new RegExp(`\\b${escapeRegExp(skill.keyword)}\\b`, 'i');
    if (re.test(text)) {
      // If the user already has this skill on their resume, it's a strength to refine;
      // if not, it's a gap to prepare harder for.
      const hasSkill = resumeSkills.has(skill.keyword.toLowerCase());
      const reason = hasSkill
        ? `Required skill â€” reinforce what you already know`
        : `Required in JD â€” not prominent in your resume`;
      add(`${skill.display} interview questions â€” complete guide`, reason, 2, 'technical', !hasSkill);
    }
  }

  // â”€â”€ PRIO 3 Â· Keyword-based from JD high-frequency words â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const words = jobDescription
    .toLowerCase()
    .replace(/[^a-z0-9+\s.-]/g, ' ')
    .split(/\s+/)
    .map(w => w.trim())
    .filter(w => w.length >= 5 && !STOPWORDS.has(w) && !/^\d+$/.test(w));

  const counts = new Map<string, number>();
  for (const w of words) counts.set(w, (counts.get(w) || 0) + 1);
  const topWords = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([w]) => w);
  for (const w of topWords) {
    add(`${w} â€” interview guide`, `High-frequency keyword in job description: "${w}"`, 3, 'keyword');
  }

  return items.sort((a, b) => a.priority - b.priority);
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Component
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

/**
 * Load every job the user has prepared for — both legacy optimizations
 * (llm_optimization_levelX) and agent-flow artifacts (job_applications).
 *
 * The page previously only sourced from llm_optimization_levelX via
 * getOptimizationHistory, so anything optimized through the AI Agent (which
 * writes only to job_applications) never appeared here. We synthesise rows
 * for those agent-only jobs and de-dupe by (title|company) so refines of
 * the same role do not multiply playlists.
 */
async function loadInterviewJobList(
  fallbackJob: string,
  fallbackCompany: string,
): Promise<JobApplicationRow[]> {
  const [history, apps] = await Promise.all([
    getOptimizationHistory(100, 0),
    getJobApplications({}).catch(() => [] as JobApplication[]),
  ]);

  const fromHistory: JobApplicationRow[] = history.map((row) => ({
    id: row.optimization_id ?? '',
    job_title: row.jobTitle ?? fallbackJob,
    company_name: row.companyName ?? fallbackCompany,
    job_url: row.jobUrl ?? null,
    status: 'saved',
    created_at: row.created_at ?? new Date().toISOString(),
    job_description: undefined,
  }));

  const knownOptIds = new Set<string>();
  const knownJobIds = new Set<string>();
  const knownTitleCo = new Set<string>();
  const tcKey = (title?: string | null, co?: string | null) =>
    `${(title || '').toLowerCase().trim()}|${(co || '').toLowerCase().trim()}`;
  for (const row of history) {
    if (row.optimization_id) knownOptIds.add(String(row.optimization_id));
    if (row.job_id) knownJobIds.add(String(row.job_id));
    knownTitleCo.add(tcKey(row.jobTitle, row.companyName));
  }

  const seenSynthetic = new Set<string>();
  const fromAgent: JobApplicationRow[] = (apps ?? [])
    .filter((app) => {
      const hasArtifact =
        Boolean((app as { resume_version?: string }).resume_version) ||
        Boolean((app as { cover_letter_snippet?: string }).cover_letter_snippet) ||
        Boolean((app as { application_snippet?: string }).application_snippet);
      if (!hasArtifact) return false;

      const optId = (app as { optimization_id?: string }).optimization_id;
      if (optId && knownOptIds.has(String(optId))) return false;
      const jid = (app as { job_id?: string }).job_id;
      if (jid && knownJobIds.has(String(jid))) return false;

      const key = tcKey(app.job_title, app.company_name);
      if (knownTitleCo.has(key)) return false;
      if (seenSynthetic.has(key)) return false;
      seenSynthetic.add(key);
      return true;
    })
    .map((app) => ({
      id: `agent-${app.id}`,
      job_title: app.job_title || fallbackJob,
      company_name: app.company_name || fallbackCompany,
      job_url: app.job_url ?? null,
      status: app.application_status || 'saved',
      created_at:
        app.applied_date ||
        (app as { created_at?: string }).created_at ||
        app.last_updated ||
        new Date().toISOString(),
      job_description: undefined,
      _agentApplication: app,
    }));

  return [...fromHistory, ...fromAgent].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

interface PrepareForInterviewProps {
  onNavigate?: (page: string) => void;
}

const PrepareForInterview: React.FC<PrepareForInterviewProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const categoryLabels = useMemo(() => ({
    behavioral: t('dashboard.prepareForInterview.categories.behavioral'),
    company: t('dashboard.prepareForInterview.categories.company'),
    role: t('dashboard.prepareForInterview.categories.role'),
    leadership: t('dashboard.prepareForInterview.categories.leadership'),
    technical: t('dashboard.prepareForInterview.categories.technical'),
    keyword: t('dashboard.prepareForInterview.categories.keyword'),
  }), [t]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState<string | null>(null);
  const [jobList, setJobList]                   = useState<JobApplicationRow[]>([]);
  const [modalJobIndex, setModalJobIndex]       = useState<number | null>(null);
  const [selectedTopic, setSelectedTopic]       = useState<CourseRecommendation | null>(null);
  const [selectedVideoId, setSelectedVideoId]   = useState<string | null>(null);
  const [videoDuration, setVideoDuration]       = useState<string | null>(null);
  const [videoChannel, setVideoChannel]         = useState<string | null>(null);
  // Tracks whether a video search is currently in-flight
  const [videoLoading, setVideoLoading]         = useState(false);
  // Alternative videos for the selected topic. The user can swap to any of
  // these from the strip below the player so a single bad #1 result doesn't
  // tank the experience.
  type AltVideo = {
    id: string;
    title: string;
    channel: string;
    duration: string;
    views?: number;
  };
  const [videoAlternatives, setVideoAlternatives] = useState<AltVideo[]>([]);
  const [pageSize, setPageSize]                 = useState(5);
  const [currentPage, setCurrentPage]           = useState(1);
  const [optContext, setOptContext]              = useState<OptimizationContext | null>(null);
  const [optContextLoading, setOptContextLoading] = useState(false);
  // Sidebar accordion â€” all collapsed by default, user opens what they need
  const [expandedCategories, setExpandedCategories] = useState<Set<TopicCategory>>(new Set());
  // Playlist sidebar starts collapsed for full immersive experience — user opens it on demand
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Track whether we are on a small (mobile) screen
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  // Auto-hide controls on desktop only; on mobile controls are always pinned visible
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobileRef = useRef(isMobile);
  useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);
  const wakeControls = useCallback(() => {
    setControlsVisible(true);
    if (isMobileRef.current) return; // never auto-hide on mobile
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 4000);
  }, []);
  // Clean up timer on unmount / when player closes
  useEffect(() => {
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, []);
  // Reset controls when player opens
  useEffect(() => {
    if (modalJobIndex != null) {
      setSidebarOpen(false);   // always start collapsed for immersive view
      wakeControls();
    } else {
      setControlsVisible(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalJobIndex]);
  const toggleCategory = (cat: TopicCategory) =>
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });

  const totalPages    = Math.max(1, Math.ceil(jobList.length / pageSize));
  const safePage      = Math.min(Math.max(1, currentPage), totalPages);
  const paginatedJobs = useMemo(
    () => jobList.slice((safePage - 1) * pageSize, safePage * pageSize),
    [jobList, safePage, pageSize]
  );
  const startItem = (safePage - 1) * pageSize + 1;
  const endItem   = Math.min(safePage * pageSize, jobList.length);
  const jobForModal = modalJobIndex != null ? jobList[modalJobIndex] ?? null : null;

  // â”€â”€ Load history â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true); setError(null);
      try {
        const list = await loadInterviewJobList(
          t('dashboard.prepareForInterview.job'),
          t('dashboard.prepareForInterview.company'),
        );
        if (mounted) setJobList(list);
      } catch (e: any) {
        if (mounted) setError(e?.message || t('dashboard.prepareForInterview.failedToLoadHistory'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  // â”€â”€ Lazy-load job description â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const job = jobForModal;
    if (!job?.job_url || job.job_description || modalJobIndex == null) return;
    const idx = modalJobIndex;
    let cancelled = false;
    (async () => {
      try {
        const { data: jd } = await supabase.from('job_details').select('scraped_json').eq('job_url', job.job_url).maybeSingle();
        const scraped = (jd?.scraped_json as Record<string, unknown>) || {};
        const desc    = (scraped.description as string) || (scraped.job_description as string);
        const req     = Array.isArray(scraped.requirements) ? (scraped.requirements as string[]).join(' ') : '';
        const sk      = Array.isArray(scraped.skills)       ? (scraped.skills as string[]).join(' ')       : '';
        let fallback  = [desc, req, sk].filter(Boolean).join('\n');
        if (!fallback) {
          try {
            const { data: sr, error: scraperErr } = await supabase.functions.invoke(
              'job-scraper',
              { body: { jobUrl: job.job_url } }
            );
            if (!scraperErr && sr) {
              const d = (sr?.data as Record<string, string>) || sr;
              fallback = (d?.description ?? d?.jobDescription) ?? '';
            }
          } catch (_) {
            // scraper down or job listing removed â€” proceed without description
          }
        }
        if (cancelled) return;
        // Even if fallback is empty we still want to proceed (topics come from title/role)
        setJobList((prev) => prev.map((j, i) => (i === idx ? { ...j, job_description: fallback } : j)));
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [modalJobIndex, jobForModal?.id, jobForModal?.job_url, jobForModal?.job_description]);

  // â”€â”€ Fetch optimization context (skill gaps, improvements, resume) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!jobForModal?.id || modalJobIndex == null) {
      setOptContext(null);
      return;
    }
    const optimizationId = jobForModal.id;
    setOptContextLoading(true);
    let cancelled = false;

    (async () => {
      try {
        // Agent-synthesised rows never have a matching llm_optimization_levelX
        // entry — short-circuit using the artifacts on the application itself
        // so we don't fire a pointless query and so the modal still has resume
        // text + match score to build a playlist from.
        if (jobForModal._agentApplication) {
          const app = jobForModal._agentApplication;
          if (!cancelled) {
            setOptContext({
              skillGaps:       [],
              improvements:    [],
              warnings:        [],
              experienceLevel: '',
              matchScore:      typeof app.confidence_score === 'number' ? app.confidence_score : 0,
              resumeText:      (app as { resume_version?: string }).resume_version || '',
            });
          }
          return;
        }

        // Note: column is "skills_gaps" (plural) in the DB schema
        const COLS = 'skills_gaps,improvements,warnings,experience_level,match_score,optimized_resume,final_resume';
        let row: Record<string, any> | null = null;

        const { data: l1 } = await supabase
          .from('llm_optimization_level1')
          .select(COLS)
          .eq('optimization_id', optimizationId)
          .maybeSingle();
        row = l1;

        if (!row) {
          const { data: l2 } = await supabase
            .from('llm_optimization_level2')
            .select(COLS)
            .eq('optimization_id', optimizationId)
            .maybeSingle();
          row = l2;
        }

        if (cancelled || !row) return;

        const toStrArr = (v: unknown) =>
          Array.isArray(v) ? v.map((s) => (typeof s === 'string' ? s : (s as any)?.text ?? '')).filter(Boolean) : [];

        setOptContext({
          skillGaps:       toStrArr(row.skills_gaps),
          improvements:    toStrArr(row.improvements),
          warnings:        toStrArr(row.warnings),
          experienceLevel: row.experience_level || '',
          matchScore:      row.match_score || 0,
          resumeText:      row.optimized_resume || row.final_resume || '',
        });
      } catch (_) { /* ignore â€” topics still work without context */ }
      finally { if (!cancelled) setOptContextLoading(false); }
    })();

    return () => { cancelled = true; };
  }, [modalJobIndex, jobForModal?.id]);

  // â”€â”€ Build recommendation list â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const recommendationsForModal: CourseRecommendation[] = useMemo(() => {
    if (!jobForModal?.job_title) return [];
    const topics = extractTopics(
      jobForModal.job_title,
      jobForModal.job_description || '',
      jobForModal.company_name,
      optContext ?? undefined
    );
    return topics.slice(0, 22).map((tp) => ({
      title:      tp.topic,
      reason:     tp.reason,
      youtubeUrl: youtubeSearchUrl(tp.topic),
      priority:   tp.priority,
      category:   tp.category,
      videoId:    null,
      duration:   null,
      channel:    null,
    }));
  }, [jobForModal, optContext]);

  // â”€â”€ Group by category for sidebar display â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const groupedRecs = useMemo(() => {
    const groups: Partial<Record<TopicCategory, CourseRecommendation[]>> = {};
    for (const rec of recommendationsForModal) {
      if (!groups[rec.category]) groups[rec.category] = [];
      groups[rec.category]!.push(rec);
    }
    return groups;
  }, [recommendationsForModal]);

  // â”€â”€ Auto-select first topic â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (modalJobIndex != null && recommendationsForModal.length > 0 && !selectedTopic) {
      setSelectedTopic(recommendationsForModal[0]);
    }
  }, [modalJobIndex, recommendationsForModal, selectedTopic]);

  // Auto-expand the sidebar section for the active topic
  useEffect(() => {
    if (selectedTopic?.category) {
      setExpandedCategories(prev => new Set([...prev, selectedTopic.category]));
    }
  }, [selectedTopic?.category]);

  const closeModal = () => {
    setModalJobIndex(null);
    setSelectedTopic(null);
    setSelectedVideoId(null);
    setVideoDuration(null);
    setVideoChannel(null);
    setOptContext(null);
  };

  // â”€â”€ Play Next / Prev â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const currentTopicIdx = useMemo(
    () => recommendationsForModal.findIndex(r => r.youtubeUrl === selectedTopic?.youtubeUrl),
    [recommendationsForModal, selectedTopic]
  );
  const hasPrev = currentTopicIdx > 0;
  const hasNext = currentTopicIdx >= 0 && currentTopicIdx < recommendationsForModal.length - 1;
  const goToPrev = () => { if (hasPrev) setSelectedTopic(recommendationsForModal[currentTopicIdx - 1]); };
  const goToNext = () => { if (hasNext) setSelectedTopic(recommendationsForModal[currentTopicIdx + 1]); };

  // â”€â”€ Fetch video for selected topic (with locale + duration filter) â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!selectedTopic) {
      setSelectedVideoId(null); setVideoDuration(null);
      setVideoChannel(null); setVideoLoading(false);
      return;
    }
    setSelectedVideoId(null); setVideoDuration(null);
    setVideoChannel(null); setVideoLoading(true);
    setVideoAlternatives([]);
    let cancelled = false;
    const query = selectedTopic.title;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke<{
          videoIds: string[];
          titles: string[];
          channels: string[];
          durations: string[];
          views?: number[];
        }>('youtube-search', {
          body: {
            query,
            category: selectedTopic.category,
            locale,
            minDurationMinutes: 4,
            maxResults: 5,
          },
        });
        if (cancelled) return;
        const ids       = data?.videoIds  ?? [];
        const titles    = data?.titles    ?? [];
        const chans     = data?.channels  ?? [];
        const durations = data?.durations ?? [];
        const views     = data?.views     ?? [];
        const alts: AltVideo[] = ids.map((id, i) => ({
          id,
          title:    titles[i]    ?? '',
          channel:  chans[i]     ?? '',
          duration: durations[i] ?? '',
          views:    views[i],
        }));
        setVideoAlternatives(alts);
        const first = alts[0];
        setSelectedVideoId(first?.id ?? null);
        setVideoDuration(first?.duration ?? null);
        setVideoChannel(first?.channel ?? null);
      } catch (_) {
        if (!cancelled) {
          setSelectedVideoId(null);
          setVideoDuration(null);
          setVideoAlternatives([]);
        }
      } finally {
        if (!cancelled) setVideoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTopic?.title, locale]);

  // Helper to swap to an alternative video without re-querying YouTube.
  const swapToAlternative = useCallback((alt: AltVideo) => {
    setSelectedVideoId(alt.id);
    setVideoDuration(alt.duration);
    setVideoChannel(alt.channel);
  }, []);

  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Render helpers
  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const reloadJobs = () => {
    setError(null); setLoading(true);
    loadInterviewJobList(
      t('dashboard.prepareForInterview.job'),
      t('dashboard.prepareForInterview.company'),
    )
      .then((list) => setJobList(list))
      .catch((e) => setError(e?.message ?? t('dashboard.prepareForInterview.couldNotLoad')))
      .finally(() => setLoading(false));
  };

  // â”€â”€ Loading â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (loading) {
    if (Capacitor.isNativePlatform()) return null;
    return (
      <div className="flex flex-1 items-center justify-center w-full" style={{ minHeight: 'calc(100vh - 48px)' }}>
        <LoadingSpinner size="lg" showText={false} />
      </div>
    );
  }

  // â”€â”€ Error â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (error) {
    return (
      <div className="flex flex-col h-full w-full min-w-0"
        style={Capacitor.isNativePlatform() ? { padding: '12px' } : { padding: '12px 16px 24px' }}>
        <div className="flex-1 flex items-center justify-center min-h-[300px]">
          <div className="vscode-bg-secondary rounded-xl border vscode-border p-8 text-center max-w-md">
            <AlertTriangle className="w-10 h-10 text-[var(--vscode-error)] mx-auto mb-4" />
            <p className="text-sm font-semibold vscode-text-primary mb-2">{t('dashboard.prepareForInterview.couldNotLoad')}</p>
            <p className="text-xs vscode-text-secondary mb-6">{error}</p>
            <button type="button" onClick={reloadJobs}
              className="px-4 py-2 rounded-lg bg-[var(--vscode-primary)] text-[var(--vscode-primary-text)] font-semibold text-sm hover:opacity-90 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4" />
              {t('dashboard.prepareForInterview.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // â”€â”€ Empty â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (jobList.length === 0) {
    return (
      <div className="flex flex-col h-full w-full min-w-0"
        style={Capacitor.isNativePlatform() ? { padding: '12px' } : { padding: '12px 16px 24px' }}>
        <div className="flex-1 flex items-center justify-center min-h-[300px]">
          <div className="vscode-bg-secondary rounded-xl border vscode-border p-8 md:p-12 text-center max-w-md">
            <Video className="w-12 h-12 md:w-16 md:h-16 vscode-text-tertiary mx-auto mb-4" />
            <h3 className="text-lg md:text-xl font-semibold vscode-text-primary mb-2">{t('dashboard.prepareForInterview.noJobsTitle')}</h3>
            <p className="text-sm md:text-base vscode-text-secondary mb-6">{t('dashboard.prepareForInterview.noJobsDesc')}</p>
            <button type="button" onClick={() => onNavigate?.('agent')}
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[var(--vscode-primary)] text-[var(--vscode-primary-text)] font-semibold text-sm hover:opacity-90 min-h-[44px] touch-manipulation">
              {t('dashboard.prepareForInterview.optimizeResume')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
  //  COURSE PLAYER â€” renders as a full page when a job is selected
  // â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
  if (modalJobIndex != null && jobForModal) {
    // On mobile controls are always shown; on desktop they auto-hide
    const showControls = controlsVisible || isMobile;
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] flex flex-col overflow-hidden"
        onMouseMove={wakeControls}
        style={{ background: 'var(--vscode-bg-primary)' }}
        onTouchStart={wakeControls}
        onKeyDown={wakeControls}
      >
        <div
          className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 transition-all duration-300"
          style={{ background: 'var(--brand-popup-header)', borderBottom: '1px solid rgba(255,255,255,0.10)', opacity: showControls ? 1 : 0, pointerEvents: showControls ? 'auto' : 'none', transform: showControls ? 'translateY(0)' : 'translateY(-100%)', paddingTop: Capacitor.isNativePlatform() ? 'max(10px, env(safe-area-inset-top))' : undefined }}
        >
          {/* Back to list button */}
          <button
            type="button"
            onClick={closeModal}
            className="shrink-0 flex items-center gap-2 px-3 py-2 min-h-[40px] rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 active:scale-95 touch-manipulation"
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
            aria-label={t('dashboard.prepareForInterview.backToJobList')}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{t('dashboard.prepareForInterview.backToJobs')}</span>
          </button>

          {/* App logo */}
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--brand-cta-bg)', boxShadow: 'var(--brand-cta-shadow)' }}
          >
            <Zap className="w-4 h-4 text-white" />
          </div>

          {/* Title */}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-white/40 uppercase tracking-widest leading-none mb-0.5">{t('dashboard.prepareForInterview.loadingInterviewPrep')}</p>
            <h2 className="text-sm font-bold text-white truncate leading-tight">
              {jobForModal.job_title}
              {jobForModal.company_name && jobForModal.company_name !== 'Company'
                ? ` Â· ${jobForModal.company_name}` : ''}
            </h2>
          </div>

          {/* Badges */}
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            {optContextLoading && (
              <span className="inline-flex items-center gap-1 text-[10px] text-white/40 bg-white/5 px-2 py-1 rounded-full border border-white/10">
                <LoadingSpinner size="xs" showText={false} /> {t('dashboard.prepareForInterview.personalising')}
              </span>
            )}
            {!optContextLoading && optContext?.skillGaps && optContext.skillGaps.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-300 bg-amber-500/20 border border-amber-500/30 px-2 py-1 rounded-full">
                ⚡ {t('dashboard.prepareForInterview.gapsTargeted', {
                  defaultValue: '{{count}} gaps targeted',
                  count: optContext.skillGaps.length,
                })}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white/50 px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <BookOpen className="w-3 h-3" />
              {t('dashboard.prepareForInterview.topicsCount_other', { count: recommendationsForModal.length })}
            </span>
          </div>

          {/* Sidebar toggle + Close */}
          <div className="flex items-center gap-2 shrink-0 ml-1">
            <button
              type="button"
              onClick={() => setSidebarOpen(o => !o)}
              title={sidebarOpen ? 'Hide playlist' : 'Show playlist'}
              className="flex items-center gap-1.5 px-2.5 py-2 min-h-[40px] rounded-xl text-xs font-semibold transition-all hover:bg-white/10 touch-manipulation"
              style={{ color: '#c4b5fd', border: '1px solid rgba(196,181,253,0.3)' }}
            >
              {sidebarOpen
                ? <ChevronLeft className="w-4 h-4" />
                : <ChevronRight className="w-4 h-4" />}
              <span>{sidebarOpen ? t('dashboard.prepareForInterview.hide') : t('dashboard.prepareForInterview.playlist')}</span>
            </button>
            <button
              type="button"
              onClick={closeModal}
              title="Back to job list"
              className="flex items-center justify-center rounded-xl transition-all hover:scale-105 active:scale-95 touch-manipulation"
              style={{ width: 40, height: 40, background: 'rgba(239,68,68,0.2)', border: '1.5px solid rgba(239,68,68,0.5)' }}
              aria-label="Close"
            >
              <X className="w-4 h-4 text-red-300" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* â”€â”€ [MID] Left playlist + center video â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex-1 min-h-0 flex overflow-hidden">


          {/* -- LEFT: Playlist / Course Curriculum -- */}
          {/* On mobile the sidebar is an absolute overlay; on desktop it's an inline panel */}
          <div
            className={`flex flex-col border-r overflow-hidden transition-all duration-200 ${isMobile ? 'absolute inset-y-0 left-0 z-20' : 'flex-shrink-0'}`}
            style={{
              width: sidebarOpen ? (isMobile ? 'min(85vw, 320px)' : 'clamp(220px, 28%, 300px)') : '0px',
              minWidth: sidebarOpen ? (isMobile ? 'min(85vw, 320px)' : 'clamp(220px, 28%, 300px)') : '0px',
              background: 'var(--vscode-bg-secondary)',
              borderColor: 'var(--brand-input-border)',
              visibility: sidebarOpen ? 'visible' : 'hidden',
              boxShadow: isMobile && sidebarOpen ? '4px 0 24px rgba(0,0,0,0.5)' : 'none',
            }}
          >
            {/* Playlist header */}
            <div
              className="flex-shrink-0 flex items-center gap-2.5 px-4 py-3 border-b"
              style={{ borderColor: 'var(--brand-input-border)', background: 'var(--brand-accent-bg)' }}
            >
              <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--brand-cta-bg)' }}>
                <BookOpen className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-white">{t('dashboard.prepareForInterview.playlist')}</p>
                <p className="text-[10px] text-white/50">
                  {t('dashboard.prepareForInterview.topicsCount_other', { count: recommendationsForModal.length })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="shrink-0 flex items-center justify-center w-6 h-6 rounded-md hover:bg-white/10 transition-colors"
                title={t('dashboard.prepareForInterview.collapsePlaylist')}
              >
                <ChevronLeft className="w-4 h-4 text-white/50" />
              </button>
            </div>

            {/* Accordion sections â€” scrollable */}
            <div className="flex-1 overflow-y-auto">
              {CATEGORY_DISPLAY_ORDER.map((cat) => {
                const catRecs = groupedRecs[cat];
                if (!catRecs || catRecs.length === 0) return null;
                const meta    = CATEGORY_META[cat];
                const CatIcon = meta.Icon;
                const isOpen  = expandedCategories.has(cat);
                const hasActive = catRecs.some(r => r.youtubeUrl === selectedTopic?.youtubeUrl);
                return (
                  <div key={cat} style={{ borderBottom: '1px solid rgba(128,128,128,0.12)' }}>
                    <button
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className="w-full flex items-center gap-2.5 px-3 py-3 text-left transition-colors"
                      style={{ background: hasActive ? 'var(--brand-accent-bg)' : isOpen ? 'rgba(128,128,128,0.05)' : 'transparent' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = hasActive ? 'var(--brand-accent-bg)' : 'rgba(128,128,128,0.07)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = hasActive ? 'var(--brand-accent-bg)' : isOpen ? 'rgba(128,128,128,0.05)' : 'transparent'; }}
                    >
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 border ${meta.bgColor}`}>
                        <CatIcon className={`w-3 h-3 ${meta.textColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold uppercase tracking-wide leading-none"
                          style={{ color: hasActive ? 'var(--vscode-text-primary)' : 'var(--vscode-text-secondary)' }}>
                          {meta.label}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                          {catRecs.length} topic{catRecs.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <ChevronDown
                        className="w-3.5 h-3.5 shrink-0 transition-transform duration-200"
                        style={{ color: '#94a3b8', transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                      />
                    </button>

                    {isOpen && catRecs.map((rec, idx) => {
                      const isActive = selectedTopic?.youtubeUrl === rec.youtubeUrl;
                      return (
                        <button
                          key={rec.youtubeUrl}
                          type="button"
                          onClick={() => setSelectedTopic(rec)}
                          className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left touch-manipulation relative"
                          style={{
                            background: isActive ? 'var(--brand-accent-bg)' : 'transparent',
                            borderBottom: '1px solid rgba(128,128,128,0.07)',
                            transition: 'background 0.13s',
                          }}
                          onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(128,128,128,0.06)'; }}
                          onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                          {isActive && (
                            <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r" style={{ background: 'var(--brand-cta-bg)' }} />
                          )}
                          <div
                            className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                            style={isActive
                              ? { background: 'var(--brand-cta-bg)', color: '#fff' }
                              : { border: '1.5px solid rgba(148,163,184,0.4)', color: '#94a3b8' }}
                          >
                            {isActive ? <Play className="w-2.5 h-2.5 fill-white text-white ml-px" /> : idx + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold line-clamp-2 leading-snug"
                              style={{ color: isActive ? '#c4b5fd' : '#e2e8f0' }}>
                              {rec.title.replace(/\s+â€”\s+.*$/i,'').replace(/\s+complete guide.*$/i,'').replace(/\s+full guide.*$/i,'')}
                            </p>
                            <p className="text-xs mt-0.5 line-clamp-1"
                              style={{ color: '#94a3b8' }}>
                              {rec.reason}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* â”€â”€ CENTER: Video player (fills remaining space) â”€â”€ */}
          {/* Mobile scrim â€” tap to close sidebar */}
          {isMobile && sidebarOpen && (
            <div
              className="absolute inset-0 z-10 bg-black/60"
              onClick={() => setSidebarOpen(false)}
              aria-hidden
            />
          )}

          <div className="flex-1 min-w-0 flex flex-col bg-black overflow-hidden relative">
            {/* Show-playlist tab â€” visible only when sidebar is collapsed */}
            {!sidebarOpen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1.5 py-3 px-2 rounded-r-xl text-xs font-semibold transition-all hover:opacity-90 active:scale-95 writing-mode-vertical"
                style={{ background: 'var(--brand-cta-bg)', boxShadow: 'var(--brand-cta-shadow)' }}
                title="Show playlist"
              >
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            )}

            {/* Now-playing header — gives the player a clear identity */}
            {selectedTopic && (selectedVideoId || videoLoading) && (
              <div
                className="shrink-0 px-4 py-2.5 border-b flex items-center gap-3 min-w-0"
                style={{ background: 'rgba(15,23,42,0.92)', borderColor: 'rgba(255,255,255,0.06)' }}
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                  style={{ background: 'var(--brand-cta-bg)' }}>
                  <Play className="w-4 h-4 fill-white text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 leading-none mb-1">Now playing</p>
                  <p className="text-sm font-semibold text-white truncate leading-tight">
                    {selectedTopic.title.replace(/\s+—\s+.*$/i,'').replace(/\s+complete guide.*$/i,'').replace(/\s+full guide.*$/i,'')}
                  </p>
                  {(videoChannel || videoDuration) && (
                    <p className="text-[11px] text-white/50 truncate mt-0.5">
                      {videoChannel}{videoChannel && videoDuration ? ' · ' : ''}{videoDuration}
                    </p>
                  )}
                </div>
                {selectedVideoId && (
                  <a
                    href={`https://www.youtube.com/watch?v=${selectedVideoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] font-semibold text-white/60 hover:text-white inline-flex items-center gap-1 shrink-0"
                    title={t('dashboard.prepareForInterview.openInYouTube')}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t('dashboard.prepareForInterview.youtube', 'YouTube')}
                  </a>
                )}
              </div>
            )}

            <div className="flex-1 min-h-0 flex items-center justify-center">
            {selectedVideoId ? (
              /* Video loaded */
              <iframe
                key={selectedVideoId}
                title={selectedTopic?.title ?? 'Interview preparation'}
                src={`https://www.youtube.com/embed/${selectedVideoId}?rel=0&modestbranding=1&autoplay=1`}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : videoLoading ? (
              /* Search in progress */
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 rounded-full border-2 border-white/10 flex items-center justify-center">
                    <Play className="w-6 h-6 text-white/20" />
                  </div>
                  <div className="absolute inset-0 rounded-full border-t-2 border-[#6C47FF] animate-spin" />
                </div>
                <p className="text-sm font-semibold text-white/60">{t('dashboard.prepareForInterview.searchingContent')}</p>
                {selectedTopic && (
                  <p className="text-xs text-white/30 max-w-xs text-center">
                    {selectedTopic.title.replace(/\s+.{1,3}\s+.*$/i, '')}
                  </p>
                )}
              </div>
            ) : selectedTopic ? (
              /* Search done â€” no qualifying video found */
              <div className="flex flex-col items-center gap-5 max-w-sm text-center px-6">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-input-border)' }}>
                  <ExternalLink className="w-7 h-7" style={{ color: '#8B6FFF' }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white/80 mb-1">{t('dashboard.prepareForInterview.noEmbeddableVideo')}</p>
                  <p className="text-xs text-white/40 leading-relaxed">
                    {t('dashboard.prepareForInterview.noEmbeddableVideoDesc', 'Our filters did not find a qualifying video. Search YouTube directly for this topic.')}
                  </p>
                </div>
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(selectedTopic.title.replace(/\s+.{1,3}\s+.*$/i, ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                  style={{ background: 'var(--brand-cta-bg)', boxShadow: 'var(--brand-cta-shadow)' }}
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('dashboard.prepareForInterview.openYouTubeSearch', 'Open YouTube Search')}
                </a>
              </div>
            ) : (
              /* Nothing selected yet */
              <div className="flex flex-col items-center gap-3 text-white/25 p-8 text-center">
                <Play className="w-16 h-16" />
                <p className="text-sm">{t('dashboard.prepareForInterview.selectTopicFromPlaylist')}</p>
              </div>
            )}
            </div>

            {/* Alternative videos strip — lets the user swap if the top result is bad */}
            {videoAlternatives.length > 1 && (
              <div
                className="shrink-0 border-t flex items-stretch gap-2 overflow-x-auto px-3 py-2.5"
                style={{ background: 'rgba(15,23,42,0.92)', borderColor: 'rgba(255,255,255,0.06)' }}
              >
                <div className="flex items-center text-[10px] font-bold uppercase tracking-wider text-white/40 shrink-0 pr-1">
                  Alt videos
                </div>
                {videoAlternatives.map((alt) => {
                  const isActive = alt.id === selectedVideoId;
                  return (
                    <button
                      key={alt.id}
                      type="button"
                      onClick={() => swapToAlternative(alt)}
                      className="shrink-0 w-40 text-left rounded-lg overflow-hidden transition-all hover:opacity-100 focus:outline-none"
                      style={{
                        background: isActive ? 'var(--brand-cta-bg)' : 'rgba(255,255,255,0.04)',
                        border: isActive ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.06)',
                        opacity: isActive ? 1 : 0.85,
                      }}
                      title={`${alt.title}${alt.channel ? ' — ' + alt.channel : ''}`}
                    >
                      <div className="relative w-full h-20 bg-black">
                        <img
                          src={`https://i.ytimg.com/vi/${alt.id}/mqdefault.jpg`}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                        {alt.duration && (
                          <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white"
                            style={{ background: 'rgba(0,0,0,0.7)' }}>
                            {alt.duration}
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1.5">
                        <p className="text-[11px] font-semibold text-white line-clamp-2 leading-snug">
                          {alt.title || 'Untitled'}
                        </p>
                        {alt.channel && (
                          <p className="text-[10px] text-white/50 truncate mt-0.5">{alt.channel}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* â”€â”€ [BOT] Navigation bar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div
          className="flex-shrink-0 flex items-center gap-2 sm:gap-4 px-3 sm:px-4 py-2 sm:py-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] transition-all duration-300"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'var(--brand-popup-header)',
            opacity: showControls ? 1 : 0, pointerEvents: showControls ? 'auto' : 'none', transform: showControls ? 'translateY(0)' : 'translateY(100%)',
          }}
        >
          {/* Currently playing info */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {selectedTopic ? (
              <>
                {(() => {
                  const meta = CATEGORY_META[selectedTopic.category];
                  const BadgeIcon = meta.Icon;
                  return (
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full border shrink-0 ${meta.bgColor} ${meta.textColor}`}>
                      <BadgeIcon className="w-2.5 h-2.5" />
                      {meta.label}
                    </span>
                  );
                })()}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-white truncate leading-tight">
                    {selectedTopic.title.replace(/\s+â€”\s+.*$/i,'').replace(/\s+complete guide.*$/i,'')}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {videoDuration && (
                      <span className="text-[10px] text-white/50 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />{videoDuration}
                      </span>
                    )}
                    {videoChannel && (
                      <span className="text-[10px] text-white/40 truncate">{videoChannel}</span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-xs text-white/30">No topic selected</p>
            )}
          </div>

          {/* Position counter */}
          {currentTopicIdx >= 0 && (
            <span className="text-xs font-bold text-white/50 shrink-0 tabular-nums">
              {currentTopicIdx + 1} / {recommendationsForModal.length}
            </span>
          )}

          {/* Prev / Next buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={goToPrev}
              disabled={!hasPrev}
              className="flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-xl text-xs font-semibold text-white transition-all disabled:opacity-25 disabled:cursor-not-allowed touch-manipulation"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)' }}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="hidden xs:inline sm:inline">{t('dashboard.prepareForInterview.prev')}</span>
            </button>

            <button
              type="button"
              onClick={goToNext}
              disabled={!hasNext}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 min-h-[40px] rounded-xl text-xs font-semibold text-white transition-all disabled:opacity-25 disabled:cursor-not-allowed hover:opacity-90 active:scale-95 touch-manipulation"
              style={{ background: hasNext ? 'var(--brand-cta-bg)' : 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              <span>{t('dashboard.prepareForInterview.playNext')}</span>
              <Play className="w-3.5 h-3.5 fill-white" />
            </button>

            {selectedTopic && (
              <a
                href={selectedTopic.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-xl text-xs font-semibold text-white/70 transition-all hover:text-white touch-manipulation"
                style={{ border: '1px solid rgba(255,255,255,0.15)' }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t('dashboard.prepareForInterview.youtube', 'YouTube')}
              </a>
            )}
          </div>
        </div>
      </div>
    , document.body);
  }

  // -- NATIVE layout --
  if (Capacitor.isNativePlatform()) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', padding: '12px 12px 100px', gap: 12, overflowY: 'auto', flex: 1 }}>

        {/* Stats chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', borderRadius: 20, padding: '6px 14px' }}>
            <Video style={{ width: 14, height: 14, color: 'var(--vscode-primary)' }} />
            <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--vscode-primary)' }}>{jobList.length}</span>
            <span style={{ fontSize: 11, color: 'var(--vscode-text-secondary)' }}>{t('dashboard.prepareForInterview.totalJobs')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)', borderRadius: 20, padding: '6px 14px' }}>
            <span style={{ fontSize: 11, color: 'var(--vscode-text-secondary)' }}>{t('dashboard.prepareForInterview.curatedPlaylistsHint')}</span>
          </div>
        </div>

        {/* Individual job cards */}
        {paginatedJobs.map((job, i) => {
          const globalIndex = (safePage - 1) * pageSize + i;
          return (
            <div key={job.id} style={{ background: 'var(--vscode-bg-elevated)', borderRadius: 16, border: '1px solid var(--vscode-border-primary)', padding: 16, boxShadow: 'var(--vscode-shadow-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--vscode-primary)' }}>
                  <Video style={{ width: 18, height: 18 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--vscode-text-primary)', marginBottom: 3, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {job.job_title}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--vscode-text-secondary)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.company_name || '—'}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)' }}>
                    {new Date(job.created_at).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: '2-digit' })}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setModalJobIndex(globalIndex)}
                style={{ width: '100%', minHeight: 44, borderRadius: 12, background: 'var(--vscode-bg-tertiary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, WebkitTapHighlightColor: 'transparent', boxSizing: 'border-box' }}
                aria-label={t('dashboard.prepareForInterview.ariaPrepareForInterview')}
              >
                <Video style={{ width: 16, height: 16, color: 'var(--vscode-text-primary)' }} />
                {t('dashboard.prepareForInterview.startInterviewPrep')}
              </button>
            </div>
          );
        })}

        {/* Pagination pills */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, paddingTop: 4 }}>
            <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
              style={{ minHeight: 40, paddingLeft: 20, paddingRight: 20, borderRadius: 20, border: '1.5px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: safePage <= 1 ? 0.4 : 1, WebkitTapHighlightColor: 'transparent' }}>
              {"<"} {t('dashboard.prepareForInterview.prev')}
            </button>
            <span style={{ fontSize: 12, color: 'var(--vscode-text-secondary)' }}>
              {t('dashboard.prepareForInterview.pageOf', { current: safePage, total: totalPages })}
            </span>
            <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
              style={{ minHeight: 40, paddingLeft: 20, paddingRight: 20, borderRadius: 20, border: '1.5px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: safePage >= totalPages ? 0.4 : 1, WebkitTapHighlightColor: 'transparent' }}>
              {t('dashboard.prepareForInterview.next')} {">"}
            </button>
          </div>
        )}

      </div>
    );
  }

  // -- WEB layout — page title lives in WorkspaceShell topbar (like Activity / Jobs)
  return (
    <div
      className="ggj-light-palette flex flex-col h-full overflow-hidden w-full min-w-0"
      style={{ padding: '12px 16px 24px' }}
    >
      <p className="text-xs sm:text-sm vscode-text-secondary mb-3 flex-shrink-0 line-clamp-2">
        {t('dashboard.prepareForInterview.subtitle')}
      </p>

      <div className="flex-1 min-h-0 flex flex-col space-y-4 overflow-y-auto pb-6">
        {/* Stats row */}
        <div className="flex items-center justify-between text-xs flex-shrink-0">
          <div className="text-lg font-semibold vscode-text-primary">{t('dashboard.prepareForInterview.interviewPrep')}</div>
          <div className="text-center">
            <div className="text-sm font-bold vscode-text-primary">{jobList.length}</div>
            <div className="vscode-text-tertiary text-xs">{t('dashboard.prepareForInterview.totalJobs')}</div>
          </div>
        </div>

        {/* Job table */}
        <div className="vscode-bg-secondary rounded-xl border vscode-border overflow-hidden flex-shrink-0 flex flex-col">
          {/* Mobile: card list */}
          <div className="md:hidden flex flex-col divide-y vscode-border">
            {paginatedJobs.map((job, i) => {
              const globalIndex = (safePage - 1) * pageSize + i;
              return (
                <div key={job.id} className="flex items-center gap-3 p-4 vscode-bg-primary/20 hover:vscode-bg-hover/50 transition-colors">
                  <span className="text-sm vscode-text-tertiary font-medium shrink-0 w-7">{startItem + i}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold vscode-text-primary break-words line-clamp-2">{job.job_title}</p>
                    <p className="text-xs vscode-text-secondary mt-0.5 truncate">{job.company_name || 'â€”'}</p>
                    <p className="text-[10px] vscode-text-tertiary mt-0.5">
                      {new Date(job.created_at).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: '2-digit' })}
                    </p>
                  </div>
                  <button type="button" onClick={() => setModalJobIndex(globalIndex)}
                    className="shrink-0 inline-flex items-center justify-center gap-1.5 min-h-[44px] px-3 rounded-xl text-white transition-opacity hover:opacity-90 touch-manipulation"
                    style={{ background: 'var(--vscode-primary)' }}
                    aria-label={t('dashboard.prepareForInterview.ariaPrepareForInterview')}>
                    <Video className="w-5 h-5" />
                    <span className="text-xs font-semibold">{t('dashboard.prepareForInterview.start')}</span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Desktop: enriched table — mirrors My Activities style */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="vscode-bg-primary/50 border-b vscode-border text-xs font-extrabold vscode-text-primary uppercase tracking-wide">
                  <th className="py-3 px-4 w-12">{t('dashboard.prepareForInterview.sno')}</th>
                  <th className="py-3 px-4">{t('dashboard.prepareForInterview.jobDetails')}</th>
                  <th className="py-3 px-4">{t('dashboard.prepareForInterview.company')}</th>
                  <th className="py-3 px-4 whitespace-nowrap">{t('dashboard.prepareForInterview.date')}</th>
                  <th className="py-3 px-4 whitespace-nowrap">{t('dashboard.prepareForInterview.prepTopics')}</th>
                  <th className="py-3 px-4 whitespace-nowrap">{t('dashboard.prepareForInterview.prepStatus')}</th>
                  <th className="py-3 px-4 w-20">{t('dashboard.prepareForInterview.action')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedJobs.map((job, i) => {
                  const globalIndex = (safePage - 1) * pageSize + i;
                  return (
                    <tr key={job.id} className="border-b vscode-border/50 last:border-b-0 hover:vscode-bg-hover/50 transition-colors">
                      {/* S.NO */}
                      <td className="py-3 px-4 text-sm vscode-text-secondary font-medium">{startItem + i}</td>

                      {/* Job Details — title + "Remote" sub-line */}
                      <td className="py-3 px-4 max-w-[220px]">
                        <p className="text-sm font-semibold vscode-text-primary truncate" title={job.job_title}>{job.job_title}</p>
                        <p className="text-xs vscode-text-tertiary mt-0.5">{t('dashboard.prepareForInterview.remote')}</p>
                      </td>

                      {/* Company */}
                      <td className="py-3 px-4 text-sm vscode-text-secondary max-w-[160px] truncate" title={job.company_name}>{job.company_name || '\u2014'}</td>

                      {/* Date */}
                      <td className="py-3 px-4 text-sm vscode-text-tertiary whitespace-nowrap">
                        {new Date(job.created_at).toLocaleDateString(locale, { month: 'numeric', day: 'numeric', year: '2-digit' })}
                      </td>

                      {/* Prep Topics badge */}
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-700/50">
                          <BookOpen className="w-3 h-3" />
                          {t('dashboard.prepareForInterview.topicsBadge')}
                        </span>
                      </td>

                      {/* Prep Status badge */}
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700/50">
                          <Play className="w-2.5 h-2.5 fill-current" />
                          {t('dashboard.prepareForInterview.prepReady')}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="py-3 px-4">
                        <button type="button" onClick={() => setModalJobIndex(globalIndex)}
                          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 rounded-lg text-white transition-opacity hover:opacity-90 touch-manipulation"
                          style={{ background: 'var(--vscode-primary)' }}
                          aria-label={t('dashboard.prepareForInterview.ariaPrepareForInterview')}
                          title={t('dashboard.prepareForInterview.startInterviewPrep')}>
                          <Video className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

                    {/* Pagination */}
          <div className="border-t vscode-border px-3 sm:px-4 py-3 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3 vscode-bg-primary/30">
            <span className="text-sm vscode-text-secondary order-2 sm:order-1">
              {t('dashboard.prepareForInterview.showing', { start: startItem, end: endItem, total: jobList.length })}
            </span>
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 order-1 sm:order-2">
              <label className="flex items-center gap-2 text-sm vscode-text-secondary">
                <span className="whitespace-nowrap">{t('dashboard.prepareForInterview.perPage')}</span>
                <select value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="py-2.5 sm:py-1.5 px-3 rounded-xl sm:rounded border vscode-border vscode-bg-primary vscode-text-primary text-sm min-w-[4rem] min-h-[44px] sm:min-h-0 touch-manipulation"
                  aria-label={t('dashboard.prepareForInterview.itemsPerPage')}>
                  {PAGE_SIZE_OPTIONS.map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                  className="min-h-[44px] min-w-[44px] sm:min-w-0 py-2.5 sm:py-1.5 px-4 rounded-xl sm:rounded border vscode-border vscode-bg-primary vscode-text-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation">
                  {t('dashboard.prepareForInterview.prev')}
                </button>
                <span className="text-sm vscode-text-tertiary px-2 min-w-[6rem] text-center py-2">
                  {t('dashboard.prepareForInterview.pageOf', { current: safePage, total: totalPages })}
                </span>
                <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                  className="min-h-[44px] min-w-[44px] sm:min-w-0 py-2.5 sm:py-1.5 px-4 rounded-xl sm:rounded border vscode-border vscode-bg-primary vscode-text-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation">
                  {t('dashboard.prepareForInterview.next')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default React.memo(PrepareForInterview);